import { createContext, useContext, ReactNode, useState, useEffect } from 'react'

import AnalyticsMonitor from './AnalyticsMonitor'

import { ANALYTICS_TRACKING_KEY, AnalyticsTrackingItem } from '@/types/analytics_monitor.type'

interface AnalyticsMonitorContextType {
  trackingItems: AnalyticsTrackingItem[]
  isPending: (conversationId: string) => boolean
  addTrackingItem: (item: AnalyticsTrackingItem) => void
}

const AnalyticsMonitorContext = createContext<AnalyticsMonitorContextType | undefined>(undefined)

export const AnalyticsCachingProvider = ({ children }: { children: ReactNode }) => {
  const [trackingItems, setTrackingItems] = useState<AnalyticsTrackingItem[]>([])

  useEffect(() => {
    const loadFromStorage = () => {
      const stored = localStorage.getItem(ANALYTICS_TRACKING_KEY)
      if (stored) {
        try {
          const items: AnalyticsTrackingItem[] = JSON.parse(stored)
          setTrackingItems(items)
        } catch (error) {
          console.error('Error parsing Analytics tracking data:', error)
          localStorage.removeItem(ANALYTICS_TRACKING_KEY)
        }
      } else {
        setTrackingItems([])
      }
    }

    loadFromStorage()
    const interval = setInterval(loadFromStorage, 3000)
    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === ANALYTICS_TRACKING_KEY) {
        if (e.newValue) {
          try {
            const items: AnalyticsTrackingItem[] = JSON.parse(e.newValue)
            setTrackingItems(items)
          } catch (error) {
            console.error('Storage event parsing error:', error)
          }
        } else {
          setTrackingItems([])
        }
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  const isPending = (conversationId: string) => {
    return trackingItems.some((item) => item.conversationId === conversationId)
  }

  const addTrackingItem = (item: AnalyticsTrackingItem) => {
    const stored = localStorage.getItem(ANALYTICS_TRACKING_KEY)
    const items: AnalyticsTrackingItem[] = stored ? JSON.parse(stored) : []

    if (!items.some((i) => i.messageId === item.messageId)) {
      items.push(item)
      localStorage.setItem(ANALYTICS_TRACKING_KEY, JSON.stringify(items))
      setTrackingItems(items)
    }
  }

  return (
    <AnalyticsMonitorContext.Provider value={{ trackingItems, isPending, addTrackingItem }}>
      {trackingItems.map((item) => (
        <AnalyticsMonitor key={item.messageId} item={item} />
      ))}
      {children}
    </AnalyticsMonitorContext.Provider>
  )
}

export const useAnalyticsTracking = () => {
  const context = useContext(AnalyticsMonitorContext)
  if (context === undefined) {
    throw new Error('useAnalyticsTracking must be used within AnalyticsCachingProvider')
  }
  return context
}
